import React, { useState, useEffect } from "react";
import { useTrackContext } from "../../hooks/useTrackContext";
import { Link, useLocation } from "react-router-dom";
import { ROUTES } from "../../consts";

interface ProgressBarProps {
  maxValue: number;
  currentValue: number;
}

interface IBreadcrumbs {
  title: string;
  link: string | null;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ maxValue, currentValue }) => {
  const [progress, setProgress] = useState<number>(0);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const location = useLocation();
  const {
    trackSelectionOption: { title },
    dispatch,
    progressiveBarBreadcrumbsRoutes,
    appHostedAt,
    metaObjectTrackSelectionConfig,
  } = useTrackContext();

  useEffect(() => {
    const percentage = (currentValue / maxValue) * 100;
    setProgress(percentage > 100 ? 100 : percentage);

    const uniqueItems = new Set([...progressiveBarBreadcrumbsRoutes, location.pathname]);
    if (!progressiveBarBreadcrumbsRoutes.includes(location.pathname)) {
      dispatch({
        type: "SET_PROGRESSIVE_BAR_BREADCRUMBS_ROUTE",
        payload: {
          progressiveBarBreadcrumbsRoutes: [...uniqueItems],
        },
      });
    }
  }, [currentValue, maxValue, progressiveBarBreadcrumbsRoutes, location.pathname, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);

    dispatch({
      type: "SET_WINDOW_WIDTH",
      payload: {
        windowWidth: windowWidth,
      },
    });

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch, windowWidth]);

  let breadcrumbs: IBreadcrumbs[] =
    title === "MAIN_TRACK" && windowWidth <= 768
      ? [
          { 
            title: metaObjectTrackSelectionConfig && metaObjectTrackSelectionConfig.mainTrack && metaObjectTrackSelectionConfig.mainTrack.title
              ? metaObjectTrackSelectionConfig.mainTrack.title
              : "main Track", 
            link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX) ? ROUTES.INDEX : null 
          },
          {
            title: "Drivers",
            link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES : null,
          },
          { title: "Time", link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME : null },
          { title: "Cart", link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.ReviewCart) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.ReviewCart : null },
        ]
      : title === "MAIN_TRACK"
      ? [
          {
            title:
              metaObjectTrackSelectionConfig && metaObjectTrackSelectionConfig.mainTrack && metaObjectTrackSelectionConfig.mainTrack.title
                ? metaObjectTrackSelectionConfig.mainTrack.title
                : "main Track",
            link: ROUTES.INDEX,
          },
          { title: "Age Group", link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.AGE_GROUP) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.AGE_GROUP : null },
          { title: "Race Type", link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.RACE_TYPE) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.RACE_TYPE : null },
          {
            title: "Drivers",
            link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.SEATS_RACES : null,
          },
          { title: "Time", link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.DATE_TIME : null },
          { title: "Cart", link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MAIN_TRACK.ReviewCart) ? ROUTES.INDEX + ROUTES.MAIN_TRACK.ReviewCart : null },
        ]
      : title === "MINI_TRACK"
      ? [
          { title: "mini track", link: ROUTES.INDEX },
          {
            title: "Drivers",
            link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MINI_TRACK.SEATS_RACES) ? ROUTES.INDEX + ROUTES.MINI_TRACK.SEATS_RACES : null,
          },
          { title: "Time", link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MINI_TRACK.DATE_TIME) ? ROUTES.INDEX + ROUTES.MINI_TRACK.DATE_TIME : null },
          { title: "Cart", link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.MINI_TRACK.ReviewCart) ? ROUTES.INDEX + ROUTES.MINI_TRACK.ReviewCart : null },
        ]
      : title === "JUNIOR_TRACK"
      ? [
          { title: "junior track", link: ROUTES.INDEX },
          {
            title: "Drivers",
            link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.JUNIOR_TRACK.SEATS_RACES) ? ROUTES.INDEX + ROUTES.JUNIOR_TRACK.SEATS_RACES : null,
          },
          {
            title: "Time",
            link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.JUNIOR_TRACK.DATE_TIME) ? ROUTES.INDEX + ROUTES.JUNIOR_TRACK.DATE_TIME : null,
          },
          { title: "Cart", link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.JUNIOR_TRACK.ReviewCart) ? ROUTES.INDEX + ROUTES.JUNIOR_TRACK.ReviewCart : null },
        ]
      : title === "RACING_GAMES_TRACK"
      ? [
          {
            title: "Select your Experience",
            link: null,
          },
          {
            title: "Preview",
            link: null,
          },
        ]
      : title === "BATTLE_KART_TRACK"
      ? [
          {
            title: "battleKart",
            link: ROUTES.INDEX,
          },
          {
            title: "Players",
            link: ROUTES.INDEX + ROUTES.BATTLE_KART_TRACK.PLAYERS_SESSIONS,
          },
          {
            title: "Time",
            link: ROUTES.INDEX + ROUTES.BATTLE_KART_TRACK.DATE_TIME,
          },
          {
            title: "Cart",
            link: ROUTES.INDEX + ROUTES.BATTLE_KART_TRACK.ReviewCart,
          },
        ]
      : [
          { title: windowWidth <= 768 ? "VR" : "virtual reality", link: ROUTES.INDEX },
          {
            title: "Players",
            link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.VIRTUAL_REALITY.PLAYERS_SESSIONS)
              ? ROUTES.INDEX + ROUTES.VIRTUAL_REALITY.PLAYERS_SESSIONS
              : ROUTES.INDEX + ROUTES.VIRTUAL_REALITY.PLAYERS_SESSIONS,
          },
          {
            title: "Time",
            link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.VIRTUAL_REALITY.DATE_TIME) ? ROUTES.INDEX + ROUTES.VIRTUAL_REALITY.DATE_TIME : null,
          },
          {
            title: "Cart",
            link: progressiveBarBreadcrumbsRoutes.includes(ROUTES.INDEX + ROUTES.VIRTUAL_REALITY.ReviewCart) ? ROUTES.INDEX + ROUTES.VIRTUAL_REALITY.ReviewCart : null,
          },
        ];

  const isMobileLarge = windowWidth >= 425 && windowWidth <= 768;

  const renderBreadcrumbs = () => {
    const handleBreadcrumbClick = (link: string) => {
      const paramsHomepage = new URLSearchParams(window.location.search);
      if (paramsHomepage.get("homepage") === "false") {
        paramsHomepage.set("homepage", "true");
        const newUrl = `${window.location.pathname}?${paramsHomepage.toString()}`;
        window.history.pushState({}, "", newUrl);
      }
      return link;
    };

    return (
      <>
        {breadcrumbs.map((breadcrumb: IBreadcrumbs, index: number) => {
            return breadcrumb.link ? (
              <Link
                to={handleBreadcrumbClick(breadcrumb.link)}
                key={index}
                className={`tw-text-white ${
                windowWidth <= 375
                    ? "tw-text-[10px]"
                    : windowWidth <= 410
                    ? "tw-text-[12px]"
                    : "tw-text-[14px]"
                } ${
                  title === "MAIN_TRACK" && windowWidth <= 600 && breadcrumb.title === "date & time" ? "tw-mr-[-24px]" : ""
                } tw-w-full tw-font-[700] tw-capitalize tw-text-center tw-max-h-[70px] md:tw-max-h-[30px] tw-h-full tw-flex tw-justify-center tw-items-center ${
                  title === "BATTLE_KART_TRACK" ? "add-grobold-font" : ""
                }`}
                style={{
                  wordBreak: "break-word",
                  textDecoration: "none",
                }}
              >
                {breadcrumb.title}
              </Link>
            ) : (
              <span
                key={index}
                className={`tw-text-white ${
                windowWidth <= 375
                    ? "tw-text-[10px]"
                    : windowWidth <= 410
                    ? "tw-text-[12px]"
                    : "tw-text-[14px]"
                }  ${
                  title === "MAIN_TRACK" && windowWidth <= 600 && breadcrumb.title === "date & time" ? "tw-mr-[-24px]" : ""
                } tw-w-full tw-font-[700] tw-capitalize tw-text-center tw-max-h-[70px] md:tw-max-h-[30px] tw-h-full tw-flex tw-justify-center tw-items-center tw-cursor-not-allowed ${
                  title === "BATTLE_KART_TRACK" ? "add-grobold-font" : ""
                }`}
                style={{
                  wordBreak: "break-word",
                }}
              >
                {breadcrumb.title}
              </span>
            );
        })}
      </>
    );
  };

  return !appHostedAt && appHostedAt !== "bosshunting" ? (
    <div className={`tw-w-full ${isMobileLarge ? "tw-h-[40px]" : "tw-h-[30px]"} md:tw-h-[30px] tw-bg-[#393B41] tw-overflow-hidden tw-relative`}>
      <div className={`tw-absolute tw-top-[0] tw-left-[0] tw-z-[999] tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center`}>{renderBreadcrumbs()}</div>
      <div
        style={{
          width: `${progress}%`,
          height: "100%",
          background:
            title === "BATTLE_KART_TRACK"
              ? "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)"
              : "linear-gradient(270deg, #F908FF 0%, #D32EFE 8.19%, #37CCFB 87.88%, #05FFFA 100%)",
        }}
        className={`${isMobileLarge ? "tw-h-[40px]" : "tw-h-[30px]"} md:tw-h-[30px] tw-relative`}
      >
        {title === "BATTLE_KART_TRACK" ? (
          <div
            className={`tw-absolute tw-top-0 tw-right-[-15px] md:tw-right-[-15px] tw-w-[40px] ${isMobileLarge ? "tw-h-[40px]" : "tw-h-[30px]"} md:tw-h-[30px]`}
            style={{
              background: "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)",
              clipPath: "polygon(0% 0%, 70% 0%, 100% 50%, 70% 100%, 0% 100%)"
            }}
          />
        ) : (
          <div
            className={`tw-absolute ${isMobileLarge ? "tw-top-[-34px]" : "tw-top-[-25px]"} md:tw-top-[-25px] ${
              isMobileLarge ? "tw-right-[-5px]" : "tw-right-[-4px]"
            } md:tw-right-[0] tw-w-[0px] tw-h-[0px] ${isMobileLarge ? "tw-border-r-[40px]" : "tw-border-r-[30px]"} md:tw-border-r-[30px] tw-border-r-[transparent] ${
              isMobileLarge ? "tw-rotate-[135deg]" : "tw-rotate-[135deg]"
            } ${isMobileLarge ? "tw-border-t-[40px]" : "tw-border-t-[30px]"} md:tw-border-t-[30px] tw-border-t-[#f908ff] ${
              isMobileLarge ? "tw-border-b-[40px]" : "tw-border-b-[30px]"
            } md:tw-border-b-[30px] tw-border-b-[transparent]`}
          />
        )}
      </div>
    </div>
  ) : null;
};

export default ProgressBar;
